import React from "react";
import { playFreq } from "@/lib/audio";

const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
// Strings from thickest (bottom of TAB) to thinnest.
export const STRINGS = [{ n: "E", midi: 40 }, { n: "A", midi: 45 }, { n: "D", midi: 50 }, { n: "G", midi: 55 }, { n: "B", midi: 59 }, { n: "e", midi: 64 }];
export const FRETS = 5;
export const noteAt = (s, f) => NAMES[(STRINGS[s].midi + f) % 12];
export const playAt = (s, f) => playFreq(440 * 2 ** ((STRINGS[s].midi + f - 69) / 12), 0.9);
export const POSITIONS = STRINGS.flatMap((_, s) => Array.from({ length: FRETS }, (_, f) => ({ s, f, id: `${s}-${f}` })));

// One fret number on a six-line TAB stave (thinnest string on top).
export const TabView = ({ s, f }) => {
  const rows = [...STRINGS].reverse();
  return (
    <svg viewBox="0 0 200 110" className="w-48" data-testid="ng-tab" role="img" aria-label="Guitar TAB with one fret number">
      <text x="8" y="40" fontSize="13" fontWeight="700" fill="currentColor" fontFamily="monospace">T</text>
      <text x="8" y="58" fontSize="13" fontWeight="700" fill="currentColor" fontFamily="monospace">A</text>
      <text x="8" y="76" fontSize="13" fontWeight="700" fill="currentColor" fontFamily="monospace">B</text>
      {rows.map((r, i) => (
        <g key={r.n}>
          <text x="30" y={19 + i * 16} fontSize="10" fill="#A8A29E" fontFamily="monospace">{r.n}</text>
          <line x1="42" y1={15 + i * 16} x2="192" y2={15 + i * 16} stroke="var(--staff-line, #78716C)" strokeWidth="1.2" />
        </g>
      ))}
      {(() => { const y = 15 + (5 - s) * 16; return (
        <g><rect x="106" y={y - 8} width="16" height="16" fill="hsl(var(--card))" /><text x="114" y={y + 5} textAnchor="middle" fontSize="15" fontWeight="700" fill="#F59E0B" fontFamily="monospace">{f}</text></g>
      ); })()}
    </svg>
  );
};

const COL = 56, ROW = 34, X0 = 40, Y0 = 18;
const fill = (id, mark) => (mark.reveal?.includes(id) ? "#22C55E" : mark.wrong === id ? "#FCA5A5" : mark.hit === id ? "#86EFAC" : "transparent");

// Clickable guitar neck: open strings plus frets 1-4, thinnest string on top.
export const Fretboard = ({ onPick = () => {}, mark = {}, labels, testId = "ng-fretboard" }) => (
  <svg viewBox={`0 0 ${X0 + COL * FRETS + 10} ${Y0 * 2 + ROW * 5 + 12}`} className="mt-4 w-full max-w-xl select-none" data-testid={testId} role="group" aria-label="Guitar fretboard, open strings to fret 4">
    <rect x={X0 + COL - 4} y={Y0 - 4} width="6" height={ROW * 5 + 8} fill="#E7E5E4" />
    {Array.from({ length: FRETS - 1 }, (_, k) => <line key={k} x1={X0 + COL * (k + 2)} y1={Y0} x2={X0 + COL * (k + 2)} y2={Y0 + ROW * 5} stroke="#A8A29E" strokeWidth="2" />)}
    {[...STRINGS].reverse().map((st, i) => <g key={st.n}><text x="8" y={Y0 + i * ROW + 4} fontSize="11" fill="#A8A29E" fontFamily="monospace">{st.n}</text><line x1={X0} y1={Y0 + i * ROW} x2={X0 + COL * FRETS} y2={Y0 + i * ROW} stroke="#D6D3D1" strokeWidth={1 + (5 - i) * 0.35} /></g>)}
    {Array.from({ length: FRETS }, (_, f) => <text key={f} x={X0 + COL * f + COL / 2} y={Y0 * 2 + ROW * 5 + 10} textAnchor="middle" fontSize="10" fill="#A8A29E" fontFamily="monospace">{f === 0 ? "open" : f}</text>)}
    {POSITIONS.map(({ s, f, id }) => {
      const cx = X0 + COL * f + COL / 2, cy = Y0 + (5 - s) * ROW;
      const tap = () => { playAt(s, f); onPick(id); };
      return (
        <g key={id} onClick={tap} style={{ cursor: "pointer" }} role="button" tabIndex={0} aria-label={`String ${6 - s}, ${f === 0 ? "open" : `fret ${f}`}`} data-testid={`fret-${id}`}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tap(); } }}>
          <rect x={cx - COL / 2 + 2} y={cy - ROW / 2} width={COL - 4} height={ROW} fill="transparent" />
          <circle cx={cx} cy={cy} r={labels?.[id] ? 14 : 8} fill={labels?.[id] ? "#F59E0B" : fill(id, mark)} stroke={fill(id, mark) === "transparent" && !labels?.[id] ? "none" : "#14532D"} />
          {labels?.[id] && <text x={cx} y={cy + 4} textAnchor="middle" fontSize={labels[id].length > 2 ? 8 : 12} fontWeight="700" fill="#0B0D12" fontFamily="monospace" data-testid={`${testId}-label-${id}`}>{labels[id]}</text>}
        </g>
      );
    })}
  </svg>
);

// Chord box: frets listed low E -> high e (number, 0 = open, "x" = not played); base = first fret shown.
export const ChordBox = ({ name, frets, base = 1, testId }) => {
  const X0 = 42, GAP = 18, FG = 22, Y0 = 34;
  return (
    <svg viewBox="0 0 160 170" className="w-36" data-testid={testId} role="img" aria-label={`${name} chord box: ${frets.join(" ")}${base > 1 ? `, from fret ${base}` : ""}`}>
      <text x={X0 + GAP * 2.5} y="14" textAnchor="middle" fontSize="14" fontWeight="700" fill="currentColor">{name}</text>
      {base === 1 ? <rect x={X0} y={Y0 - 4} width={GAP * 5} height="4" fill="currentColor" /> : <text x={X0 - 6} y={Y0 + FG / 2 + 5} textAnchor="end" fontSize="13" fontWeight="700" fill="#E7E5E4">{`${base}fr`}</text>}
      {[0, 1, 2, 3, 4].map((k) => <line key={k} x1={X0} y1={Y0 + k * FG} x2={X0 + GAP * 5} y2={Y0 + k * FG} stroke="#A8A29E" />)}
      {frets.map((f, s) => (
        <g key={s}>
          <line x1={X0 + s * GAP} y1={Y0} x2={X0 + s * GAP} y2={Y0 + FG * 4} stroke="#D6D3D1" strokeWidth={1.6 - s * 0.15} />
          {f === "x" || f === 0 ? <text x={X0 + s * GAP} y={Y0 - 9} textAnchor="middle" fontSize="11" fill="#A8A29E">{f === 0 ? "o" : "x"}</text>
            : <circle cx={X0 + s * GAP} cy={Y0 + (f - base + 0.5) * FG} r="6.5" fill="#F59E0B" />}
        </g>
      ))}
      <text x={X0 + GAP * 2.5} y="162" textAnchor="middle" fontSize="12" fill="#D6D3D1" fontFamily="monospace">E A D G B e</text>
    </svg>
  );
};

export const strum = (frets, up = false) => frets.forEach((f, s) => { if (f !== "x") setTimeout(() => playAt(s, f), (up ? 5 - s : s) * (up ? 35 : 60)); });
